import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

const SearchableSelect = ({
    options = [],
    selectedValue = null,
    onChange,
    placeholder = "Rechercher et selectionner...",
    searchPlaceholder = "Rechercher...",
    loading = false,
    disabled = false,
    maxHeight = "250px",
    showOptionMeta = true,
    // Meta on the closed trigger only. Set false to keep the dropdown's meta while
    // leaving the collapsed control uncluttered. Defaults true so existing callers
    // are unaffected.
    showTriggerMeta = true,
    getOptionLabel = (option) => option.title,
    getOptionMeta = (option) => option.limesurvey_id ? `ID: ${option.limesurvey_id}` : null,
    triggerClassName = "px-4 py-3",
    textClassName = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        const label = getOptionLabel(option) || '';
        const meta = getOptionMeta(option) || '';
        const searchLower = searchTerm.toLowerCase();
        return label.toLowerCase().includes(searchLower) ||
               String(meta).toLowerCase().includes(searchLower) ||
               (option.limesurvey_id && String(option.limesurvey_id).includes(searchTerm));
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled && !loading) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        }
    };

    const handleOptionSelect = (option) => {
        onChange(option.id);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleOptionSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    const selectedOption = options.find(option => option.id === selectedValue);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Input Field */}
            <div
                className={`w-full ${triggerClassName} ${textClassName} border border-gray-300 rounded-lg cursor-pointer transition-colors duration-200 ${
                    isOpen
                        ? 'ring-2 ring-red-500 border-red-500'
                        : 'hover:border-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                onClick={handleToggle}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        {!selectedOption ? (
                            <span className="text-gray-500">{placeholder}</span>
                        ) : (
                            <div className="flex items-center">
                                <span className="text-gray-900 font-medium truncate">
                                    {getOptionLabel(selectedOption)}
                                </span>
                                {showOptionMeta && showTriggerMeta && getOptionMeta(selectedOption) && (
                                    <span className="ml-2 text-sm text-gray-500">
                                        ({getOptionMeta(selectedOption)})
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                        {selectedOption && !disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                            </button>
                        )}
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={searchPlaceholder}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div
                        className="overflow-y-auto"
                        style={{ maxHeight }}
                    >
                        {loading ? (
                            <div className="p-4 text-center text-gray-500">
                                <FontAwesomeIcon icon={faSearch} spin className="mr-2" />
                                Chargement...
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {searchTerm ? 'Aucun resultat trouve' : 'Aucune option disponible'}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                const isSelected = option.id === selectedValue;
                                const isHighlighted = index === highlightedIndex;

                                return (
                                    <div
                                        key={option.id}
                                        className={`px-4 py-3 cursor-pointer transition-colors duration-150 ${
                                            isHighlighted
                                                ? 'bg-red-50'
                                                : 'hover:bg-gray-50'
                                        } ${isSelected ? 'bg-red-50' : ''}`}
                                        onClick={() => handleOptionSelect(option)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-gray-900">
                                                    {getOptionLabel(option)}
                                                </div>
                                                {showOptionMeta && getOptionMeta(option) && (
                                                    <div className="text-sm text-gray-500">
                                                        {getOptionMeta(option)}
                                                    </div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="w-4 h-4 text-red-600 ml-2"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
